import React, { useEffect, useState } from 'react';
import {
  ModuleBaseModel,
  ModuleBasePropsModel,
  ReportCardType,
  ReportSearchModel,
  CognitiveSearchRequestModel,
} from '@/types';
import { HeadingTag, Link, ModuleBase, ResponsiveImage, Stack, Text, Box } from '@/components';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import Close from '@/assets/close.svg';
import { searchMedia, searchReports } from '@/api';
import { useRouter } from 'next/router';
import { getSearchFilters, pushValuesToRouteQuery } from '@/utils';
import ImageIcon from '@/assets/imageIcon.svg';
import ReportModulHeader from '@/components/report/ReportModulHeader';
import Paginator from '@/components/search/Paginator';
import { ReportsSearchFilterType } from '@/components/search/SearchFilters';

export type FilterableReportGridModuleModel = ModuleBaseModel<
  {
    heading?: string;
    headingTag?: string;
    description?: string;
    availableFilters: ReportsSearchFilterType[];
    requestFilter: {
      filters: {
        name: string;
        value: number[];
      }[];
      orderBy: 'score' | 'date';
      page: number;
      pageSize: number;
      limit: number;
      publicSiteSearch: boolean;
      culture: string;
    };
    cardType: ReportCardType;
  } & ModuleBasePropsModel
>;

const FilterableReportGridModule = (data: FilterableReportGridModuleModel) => {
  const {
    props: {
      heading,
      headingTag,
      description,
      availableFilters,
      requestFilter: { orderBy, pageSize },
      cardType,
    },
  } = data;

  const router = useRouter();
  const t = useTranslations();

  const getSkeletons = (numberOfSkeletons: number) => {
    const skeletons: ReportSearchModel[] = [];
    for (let index = 0; index < numberOfSkeletons; index++) {
      skeletons.push({ reportId: index, isLoading: true } as ReportSearchModel);
    }
    return skeletons;
  };

  const [internalCardArray, setInternalCardArray] = useState<ReportSearchModel[]>(getSkeletons(pageSize));
  const [mergedFilters, setMergedFilters] = useState<string[]>([]);
  const [totalResults, setTotalResults] = useState(10);

  useEffect(() => {
    //assignValuesFromRouteQuery(router, submitFilters);
    getData();
    readMergedFilters();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query]);

  const getData = async () => {
    setInternalCardArray(getSkeletons(pageSize));

    const nameMap: any = {};
    availableFilters.forEach((filter) => {
      nameMap[filter.key] = filter.name;
    });

    const searchFilters = getSearchFilters(router, nameMap);

    const filterRequest: CognitiveSearchRequestModel = {
      filters: searchFilters,
      facetsMode: 'NoFacets',
      orderBy,
      publicSiteSearch: true,
      page: router.query.page ? Number(router.query.page) - 1 : 0,
      pageSize: pageSize,
    };

    (cardType === 'VideoCardModel' ? searchMedia(filterRequest) : searchReports(filterRequest)).then((dataResponse) => {
      setInternalCardArray(dataResponse.results);
      setTotalResults(dataResponse.totalResults);
    });
  };

  const toggleFilter = (name: string, value: string) => {
    const currentQuery = { ...router.query };
    const stringValue = value;
    if (currentQuery[name]) {
      let currentFilter = currentQuery[name];

      if (!Array.isArray(currentFilter)) {
        currentFilter = [currentFilter];
      }

      if (currentFilter.includes(stringValue)) {
        currentFilter = currentFilter.filter((item: string) => item !== stringValue);
      } else {
        currentFilter = [...currentFilter, stringValue];
      }
      pushValuesToRouteQuery(router, { [name]: currentFilter, page: null });
    } else {
      pushValuesToRouteQuery(router, { [name]: stringValue, page: null });
    }

    readMergedFilters();
  };

  const readMergedFilters = () => {
    const mergedFilters: string[] = [];
    availableFilters.forEach((filter) => {
      const currentQuery = { ...router.query };
      const stringValue = filter.key;
      if (currentQuery[stringValue])
        mergedFilters.push(
          ...(Array.isArray(currentQuery[stringValue]) ? currentQuery[stringValue] : [currentQuery[stringValue]]),
        );
    });

    setMergedFilters(mergedFilters);
  };

  const filtersExpanded = !!availableFilters.find((filter) => !!router.query[filter.key]);

  return (
    <ModuleBase data={data} noPadding className="overflow-hidden py-10 lg:py-18">
      <Stack className="container flex flex-col justify-between gap-10 lg:flex-row lg:items-stretch lg:gap-8">
        <div className="color-from-bg flex w-full flex-grow flex-col justify-between lg:w-1/2">
          {heading && <HeadingTag className="text-style-h2" tag={headingTag} heading={heading} dontAddClassFromTag />}
        </div>
        <div className="color-from-bg relative flex w-fit flex-col lg:w-1/2">
          {description && <Text className="text-style-h5 xl:w-4/5" dangerouslySetInnerHTML={{ __html: description }} />}
        </div>
      </Stack>

      <div className="container mt-10 flex flex-col lg:mt-20">
        <ReportModulHeader
          title={t('general.$filters')}
          expanded={filtersExpanded}
          className="[&_.title]:text-style-tag [&_.title]:uppercase"
        >
          <div className="flex flex-col gap-5">
            {availableFilters.length > 0 &&
              availableFilters.map((filter, index) => (
                <div key={index} className="mt-5 flex flex-col gap-3">
                  <div className="text-style-body">{filter.name}</div>
                  <div className="flex flex-wrap gap-2">
                    {filter.options.length > 0 &&
                      filter.options.map((option, index) => (
                        <button
                          key={index}
                          className={classNames(
                            'btn text-style-body border border-black !px-5 !font-normal !transition-colors',
                            mergedFilters.includes(option.id)
                              ? 'bg-black text-white'
                              : 'hover:bg-gray-2 bg-white !px-[33px] text-black',
                          )}
                          onClick={() => toggleFilter(filter.key, option.id)}
                        >
                          {option.name}

                          {mergedFilters.includes(option.id) && <Close className="h-4 w-4" />}
                        </button>
                      ))}
                  </div>
                </div>
              ))}
          </div>
        </ReportModulHeader>
        <div id="listing-grid" />

        <div className="-mx-3 mt-12 flex flex-wrap">
          {internalCardArray?.map((card, index) => (
            <div key={card.reportId || index} className="w-full md:w-1/2 lg:w-1/4 xl:w-1/5">
              <FilterableReportCard {...card} />
            </div>
          ))}
        </div>
        <Paginator
          pageSize={pageSize}
          goToElement="#listing-grid"
          totalResults={totalResults}
          queryMode
          className="lg:mt-10 [&_.btn]:!font-normal"
        />
      </div>
    </ModuleBase>
  );
};

const FilterableReportCard = (report: ReportSearchModel) => {
  return (
    <div className="flex flex-col p-3">
      {!report.isLoading ? (
        <Link link={report.reportUrl} className="flex flex-col gap-3">
          <div className="flex aspect-[366/326]">
            {report.image && (
              <ResponsiveImage
                image={report.image}
                cdnProps={{ mode: 'crop' }}
                widths={{ xs: 385, sm: 600, md: 336, lg: 366, xl: 366, xxl: 366 }}
                heights={{ xs: 324, sm: 536, md: 300, lg: 326, xl: 326, xxl: 326 }}
              />
            )}
          </div>
          <div className="text-style-tag text-red uppercase">{report.format}</div>

          <div className="text-style-h5 text-[24px] leading-[28px]">{report.title}</div>
        </Link>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex aspect-[366/326] w-full animate-pulse items-center justify-center bg-gray-300 p-10">
            <ImageIcon className="h-10 w-10 text-gray-200 dark:text-gray-600" />
          </div>
          <div className="h-5 w-2/3 animate-pulse rounded-full bg-gray-300" />
          <div className="h-18 w-4/5 animate-pulse rounded-full bg-gray-300" />
        </div>
      )}
    </div>
  );
};

export default FilterableReportGridModule;
